import { useEffect } from 'react';

import { useSyncRef } from '../syncRef';
import { ensureArray } from 'utils/array';

type TriggerKey = 'ctrl' | 'shift';

export interface MultiKeyPressParams {
  key: string;

  /**
   * Remember that ctrl on windows = command on mac here
   */
  triggerKey: TriggerKey | TriggerKey[];

  /**
   * Receive the whole event to handle/parse
   */
  onEvent?: (e: KeyboardEvent) => void;

  /**
   * A simple action to perform given the conditions met
   *
   * If you want more control over the event, use `onEvent`
   */
  action?: () => void;

  /**
   * Prevents the default browser action, if there's any
   *
   * Defaults to `TRUE`
   */
  preventDefault?: boolean;

  /**
   * Stops the event from reaching other handlers
   *
   * Useful if you have a combo inside a modal that is also used more globally
   *
   * Defaults to `FALSE`
   */
  stopPropagation?: boolean;

  /**
   * Conditionally block the whole interaction
   *
   * If false, the press will act as the hook was not called
   */
  isEnabled?: boolean;
}

const conditionChecks: { [K in TriggerKey]: (e: KeyboardEvent) => boolean } = {
  ctrl: (e) => e.ctrlKey || e.metaKey,

  shift: (e) => e.shiftKey,
};

export function useMultiKeyPress({ key, triggerKey, ...params }: MultiKeyPressParams) {
  const cache = useSyncRef({
    ...params,
  });

  const triggers = ensureArray(triggerKey);

  useEffect(() => {
    const keyHandler = (event: KeyboardEvent) => {
      const {
        action,
        onEvent,
        stopPropagation,
        isEnabled = true,
        preventDefault = true,
      } = cache.current;

      if (!isEnabled) return;

      const allTriggersMatch = triggers.every((t) => conditionChecks[t](event));

      // 'ctrl+shift+o' would trigger 'ctrl+o' matchers
      const unselectedTriggersNotUsed = Object.entries(conditionChecks)
        .filter(([trigger]) => !triggers.includes(trigger as TriggerKey))
        .every(([, checker]) => !checker(event));

      const isMatch =
        key.toUpperCase() === event.key.toUpperCase() &&
        allTriggersMatch &&
        unselectedTriggersNotUsed;

      if (!isMatch) return;

      if (preventDefault) event.preventDefault();
      if (stopPropagation) event.stopPropagation();

      onEvent?.(event);
      action?.();
    };

    document.addEventListener('keydown', keyHandler);

    return () => document.removeEventListener('keydown', keyHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, triggers.join('.')]);

  return [...ensureArray(triggerKey), key];
}
